import { Badge, Popover } from '@mantine/core';
import { useCallback, useState } from 'react';
import type { AssertionMethod } from '~/ai_tools/playwright_utils/types';
import type { AssertionPart } from '~/components/test/steps/step/operation/simple/converters';
import AssertionConfig from './AssertionConfig';

export type BaseAssertion = Exclude<AssertionMethod, 'toHaveValues' | 'toHaveJSProperty'>;

export const assertionsToDisplay: Record<
  BaseAssertion,
  { display: string; description: string; requiresValue?: boolean }
> = {
  toBeAttached: { display: 'to be attached', description: 'Checks if the element is attached to the DOM' },
  toBeChecked: { display: 'to be checked', description: 'Checks if the element is checked' },
  toBeDisabled: { display: 'to be disabled', description: 'Checks if the element is disabled' },
  toBeEditable: { display: 'to be editable', description: 'Checks if the element is editable' },
  toBeEmpty: { display: 'to be empty', description: 'Checks if the element is empty' },
  toBeEnabled: { display: 'to be enabled', description: 'Checks if the element is enabled' },
  toBeFocused: { display: 'to be focused', description: 'Checks if the element is focused' },
  toBeHidden: { display: 'to be hidden', description: 'Checks if the element is hidden' },
  toBeInViewport: { display: 'to be in viewport', description: 'Checks if the element is in the viewport' },
  toBeVisible: { display: 'to be visible', description: 'Checks if the element is visible' },
  toContainText: {
    display: 'to contain text',
    description: 'Checks if the element contains text',
    requiresValue: true,
  },
  toHaveAttribute: {
    display: 'to have attribute',
    description: 'Checks if the element has an attribute',
    requiresValue: true,
  },
  toHaveScreenshot: {
    display: 'screenshot match',
    description: 'Checks if the screenshot matches the expected screenshot',
  },
  toHaveClass: {
    display: 'to have class',
    description: 'Checks if the element has a class',
    requiresValue: true,
  },
  toHaveCount: { display: 'to have count', description: 'Checks if the element has a count', requiresValue: true },
  toHaveCSS: { display: 'to have CSS', description: 'Checks if the element has CSS', requiresValue: true },
  toHaveId: { display: 'to have ID', description: 'Checks if the element has an ID', requiresValue: true },
  toHaveText: { display: 'to have text', description: 'Checks if the element has text', requiresValue: true },
  toHaveValue: {
    display: 'to have value',
    description: 'Checks if the element has a value',
    requiresValue: true,
  },
};

export default function AssertionPart({
  assertionPart,
  onClick,
  toggleEditing,
  updateFunc,
  readonly,
}: {
  assertionPart: AssertionPart;
  onClick?: () => void;
  toggleEditing: (editing: boolean) => void;
  updateFunc: (assertionPart: AssertionPart) => Promise<void>;
  readonly?: boolean;
}) {
  const { assertion, invert } = assertionPart;
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleOpenPopover = useCallback(() => {
    if (readonly) {
      return;
    }
    onClick?.();
    toggleEditing(true);
    setIsPopoverOpen(true);
  }, [readonly, onClick, toggleEditing]);

  const handleClosePopover = useCallback(() => {
    toggleEditing(false);
    setIsPopoverOpen(false);
  }, [toggleEditing]);

  const handleSave = useCallback(
    async (newAssertionPart: AssertionPart) => {
      setIsSaving(true);
      try {
        await updateFunc(newAssertionPart);
        setIsPopoverOpen(false);
        toggleEditing(false);
      } finally {
        setIsSaving(false);
      }
    },
    [updateFunc, toggleEditing, setIsPopoverOpen],
  );

  const displayInfo = assertion ? assertionsToDisplay[assertion] : undefined;

  return (
    <Popover
      opened={isPopoverOpen}
      onClose={handleClosePopover}
      onDismiss={handleClosePopover}
      position="bottom"
      width={400}
      withArrow
      shadow="md"
      closeOnClickOutside
    >
      <Popover.Target>
        <div
          className="rounded-md hover:cursor-pointer"
          onDoubleClick={handleOpenPopover}
        >
          <div className="flex items-center gap-2">
            {invert ?
              <Badge
                color="orange"
                variant="filled"
                size="xs"
                className="bg-orange-50 px-1 py-0.5 text-xs font-medium text-orange-700 hover:bg-orange-100 hover:text-orange-800 dark:bg-orange-900/20 dark:text-orange-400 dark:hover:bg-orange-900/30 dark:hover:text-orange-300"
                classNames={{ root: 'normal-case' }}
              >
                NOT
              </Badge>
            : null}
            <div className="flex items-center space-x-1 bg-transparent">
              <div className="text-yellow-900 dark:text-yellow-600">{displayInfo?.display || 'Select assertion'}</div>
            </div>
          </div>
        </div>
      </Popover.Target>
      <Popover.Dropdown className="dark:bg-neutral-800">
        <AssertionConfig
          assertionPart={assertionPart}
          onClose={handleClosePopover}
          onSave={handleSave}
          isSaving={isSaving}
          readonly={readonly}
        />
      </Popover.Dropdown>
    </Popover>
  );
}
